import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Users, BarChart3, Settings, Languages } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import PWANotificationPrompt from "@/components/admin/PWANotificationPrompt";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t, toggle, lang } = useLang();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate("/admin/login"); };
  const isOwner = user?.role === "admin";

  const items = [
    { to: "/admin", key: "overview", label: t("admin.overview"), icon: LayoutDashboard },
    { to: "/admin/leads", key: "leads", label: t("admin.leads"), icon: Users },
    { to: "/admin/stats", key: "stats", label: "Statistics", icon: BarChart3 },
    ...(isOwner ? [{ to: "/admin/settings", key: "settings", label: "Settings", icon: Settings }] : []),
  ];

  return (
    <div className="min-h-screen bg-propertic-bg text-propertic-ink grid grid-cols-1 md:grid-cols-[260px_1fr]">
      <aside className="bg-propertic-navy text-white md:min-h-screen flex items-center justify-between md:items-stretch md:justify-start md:flex-col">
        <div className="px-4 py-3 md:p-6 md:border-b border-white/10 flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="inline-flex items-center bg-white rounded-lg px-2 py-1 md:py-1.5">
              <img src="/propertic-logo.png" alt="The Propertic" className="h-7 md:h-9 w-auto object-contain" />
            </div>
          </Link>
          <p className="hidden md:block mt-3 font-mono-brand text-propertic-gold-2/90">CRM Console</p>
        </div>
        <nav
          data-testid="admin-nav"
          className="fixed bottom-0 inset-x-0 z-40 grid md:static md:flex md:flex-col gap-1 p-1.5 md:p-3 flex-1 bg-propertic-navy border-t border-white/10 md:border-t-0 pb-[max(0.375rem,env(safe-area-inset-bottom))] md:pb-3"
          style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
        >
          {items.map((it) => (
            <NavLink
              key={it.key}
              to={it.to}
              end={it.to === "/admin"}
              data-testid={`admin-nav-${it.key}`}
              className={({ isActive }) =>
                `flex flex-col md:flex-row items-center justify-center md:justify-start gap-1 md:gap-3 px-1 md:px-4 py-1.5 md:py-2.5 text-[10px] md:text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive
                    ? "bg-propertic-gold text-propertic-navy"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`
              }
            >
              <it.icon className="w-5 h-5 md:w-4 md:h-4" />
              <span className="truncate max-w-full">{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="md:hidden px-3 flex gap-2">
          <button
            onClick={toggle}
            data-testid="admin-lang-toggle-mobile"
            className="h-9 px-3 border border-white/20 rounded-lg text-xs font-mono-brand text-white/80 flex items-center justify-center gap-1.5"
          >
            <Languages className="w-3.5 h-3.5" /> {lang === "en" ? "AR" : "EN"}
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn-mobile"
            aria-label={t("admin.sign_out")}
            className="h-9 px-3 border border-white/20 rounded-lg text-xs font-mono-brand text-white/80 hover:bg-red-500 transition-colors flex items-center justify-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" /> <span className="whitespace-nowrap">{t("admin.sign_out")}</span>
          </button>
        </div>
        <div className="hidden md:block p-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-1 font-mono-brand">{user?.role}</div>
          <div className="text-sm text-white break-all mb-4">{user?.email}</div>
          <div className="flex gap-2">
            <button
              onClick={toggle}
              data-testid="admin-lang-toggle"
              className="flex-1 h-9 border border-white/20 rounded-lg text-xs font-mono-brand text-white/80 hover:bg-white hover:text-propertic-navy transition-colors flex items-center justify-center gap-2"
            >
              <Languages className="w-3.5 h-3.5" /> {lang === "en" ? "AR" : "EN"}
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex-1 h-9 border border-white/20 rounded-lg text-xs font-mono-brand text-white/80 hover:bg-red-500 hover:border-red-500 hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> {t("admin.sign_out")}
            </button>
          </div>
        </div>
      </aside>
      <main className="p-6 pb-28 md:p-10 bg-propertic-bg text-propertic-ink">
        <Outlet />
      </main>
      <PWANotificationPrompt />
    </div>
  );
}
